import type { USAGE_TABS } from '../constants';
import type { MouseEvent } from 'react';

import { useQueryClient } from '@tanstack/react-query';

import {
  getAccountingReferencesDetailsQueryOptions,
  getSitesDetailsQueryOptions,
  getUserAnalyticsDetailsQueryOptions,
} from '~/data/analytics';

type UsageTab = (typeof USAGE_TABS)[number];

type UseTabPrefetchOptions = {
  dateRange:
    | {
        dateFrom: string;
        dateTo: string;
      }
    | undefined;
};

/**
 * Custom hook for prefetching data when hovering over usage analytics tabs.
 * This improves UX by loading data before the user clicks the tab.
 */
export function useTabPrefetch({ dateRange }: UseTabPrefetchOptions) {
  const queryClient = useQueryClient();

  const handleTabMouseDown = (event: MouseEvent<HTMLDivElement>) => {
    const newValue = (event.target as HTMLElement).id.replace(
      'tab--',
      '',
    ) as UsageTab;

    // Prefetch data for the new tab
    switch (newValue) {
      case 'costCenters':
        void queryClient.prefetchQuery(
          getAccountingReferencesDetailsQueryOptions({
            queryParams: {
              dateRange,
            },
          }),
        );
        break;

      case 'sites':
        void queryClient.prefetchQuery(
          getSitesDetailsQueryOptions({
            queryParams: {
              dateRange,
            },
          }),
        );
        break;

      case 'users':
        void queryClient.prefetchQuery(
          getUserAnalyticsDetailsQueryOptions({
            queryParams: {
              dateRange,
            },
          }),
        );
        break;
    }

    event.preventDefault();
  };

  return { handleTabMouseDown };
}
